import { useEffect, useRef } from "react";
import LoginWithButton from "@/components/common/LoginWithButton";
import facebookIcon from "../../assets/images/FacebookIcon.svg";

interface FacebookLoginButtonProps {
  appId: string;
  callback: (response: { accessToken?: string }) => void;
  buttonText: string;
  "aria-label"?: string;
}

declare global {
  interface Window {
    FB: {
      init: (params: {
        appId: string;
        version: string;
        xfbml: boolean;
        cookie: boolean;
      }) => void;
      login: (
        callback: (response: {
          authResponse?: { accessToken: string };
          status: string;
        }) => void,
        options?: { scope: string },
      ) => void;
    };
    fbAsyncInit: () => void;
  }
}

export default function FacebookLoginButton({
  appId,
  callback,
  buttonText,
  "aria-label": ariaLabel,
}: FacebookLoginButtonProps) {
  const sdkInitialised = useRef(false);

  useEffect(() => {
    if (sdkInitialised.current) return;
    sdkInitialised.current = true;

    if (!document.getElementById("fb-root")) {
      const fbRoot = document.createElement("div");
      fbRoot.id = "fb-root";
      document.body.appendChild(fbRoot);
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        version: "v18.0",
        xfbml: false,
        cookie: false,
      });
    };

    if (!document.getElementById("facebook-jssdk")) {
      const script = document.createElement("script");
      script.id = "facebook-jssdk";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [appId]);

  const handleClick = () => {
    if (!window.FB) return;
    window.FB.login(
      (response) => {
        if (response.authResponse) {
          callback({ accessToken: response.authResponse.accessToken });
        } else {
          callback({});
        }
      },
      { scope: "public_profile,email" },
    );
  };

  return (
    <LoginWithButton
      iconSrc={facebookIcon}
      onClick={handleClick}
      aria-label={ariaLabel ?? "Continue with Facebook"}
    >
      {buttonText}
    </LoginWithButton>
  );
}
